import { useState } from 'react';
import { Box, Tabs, Tab } from '@mui/material';
import { SideBySideViewProps } from './types';
import { DiscoveryPanel } from './DiscoveryPanel';
import { FeatureMatrixPanel } from './FeatureMatrixPanel';
import { CurrentSuppliersPanel } from './CurrentSuppliersPanel';
import { OfficialQuotesPanel } from './OfficialQuotesPanel';
import { PricingPanel } from './PricingPanel';
import { RoiAnalysisPanel } from './RoiAnalysisPanel';
import { DataRoomPanel } from './DataRoomPanel';
import { MutualActionPlanPanel } from './MutualActionPlanPanel';
import { SupplierSelectionTab } from './SupplierSelectionTab';
import { SupplierScoringPanel } from './SupplierScoringPanel';
import { useEngagementEditor } from './EngagementEditorContext';
import {
  EngagementStepFooter,
  EngagementTab,
  EngagementTabKey,
  hasUnansweredRequired,
  LeaveStepDialog,
  STEP_STATUS_LABELS,
  StepStatus,
  StepStatusGlyph,
  visibleTabs
} from '../EngagementSteps';

export const SideBySideView = ({ steps, stepFlow, stepStatus, shareLink }: SideBySideViewProps) => {
  const { engagement, supplierSelection, supplierScoring, unsavedDraft } = useEngagementEditor();
  const leave = (go: () => void) => (unsavedDraft ? unsavedDraft.leave(go) : go());
  const tabs = visibleTabs(steps, { shareLink }).filter(
    (tab) => (tab.key !== 'suppliers' || supplierSelection) && (tab.key !== 'scoring' || supplierScoring)
  );
  const [selectedKey, setSelectedKey] = useState<EngagementTabKey>('discovery');
  const [pendingKey, setPendingKey] = useState<EngagementTabKey | null>(null);
  const [stayRequests, setStayRequests] = useState(0);
  // Discovery is a required step, so it is always there to fall back to when the selected tab is turned off.
  const activeIndex = Math.max(
    tabs.findIndex((tab) => tab.key === selectedKey),
    0
  );
  const active = tabs[activeIndex];
  const activeKey = active.key;
  const progress = stepFlow?.requiredProgress?.[active.step ?? ''];
  const pending = tabs.find((tab) => tab.key === pendingKey);
  const currentSuppliersShown = tabs.some((tab) => tab.key === 'current_suppliers');

  const stepName = (tab: EngagementTab) => steps?.find((step) => step.key === tab.step)?.label ?? tab.label;

  // The glyph sits inside the label: MUI's icon slot would make every tab 72px tall.
  const tabLabel = (tab: EngagementTab, status?: StepStatus) =>
    status ? (
      <Box
        component="span"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
        <StepStatusGlyph
          status={status}
          current={tab.key === activeKey}
        />
        {tab.label}
      </Box>
    ) : (
      tab.label
    );

  const select = (key: EngagementTabKey) => {
    if (key !== activeKey) stepFlow?.onLeaveTab?.(activeKey);
    setSelectedKey(key);
  };

  const goTo = (key: EngagementTabKey) =>
    leave(() => {
      const target = tabs.find((tab) => tab.key === key);
      if (target && target.step !== active.step && hasUnansweredRequired(progress)) {
        setPendingKey(key);
        return;
      }
      select(key);
    });

  const continueToPending = () => {
    if (pendingKey) select(pendingKey);
    setPendingKey(null);
  };

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeKey}
          onChange={(_, key: EngagementTabKey) => goTo(key)}
          variant="scrollable"
          scrollButtons="auto">
          {tabs.map((tab) => {
            // A step whose status isn't worked out yet, as while its data loads, shows no dot rather than a wrong one.
            const status = tab.step ? stepStatus?.[tab.step] : undefined;
            return (
              <Tab
                key={tab.key}
                value={tab.key}
                label={tabLabel(tab, status)}
                title={status && STEP_STATUS_LABELS[status]}
              />
            );
          })}
        </Tabs>
      </Box>

      <Box sx={{ minHeight: 400 }}>
        {activeKey === 'discovery' && <DiscoveryPanel focusIncomplete={stayRequests} />}

        {activeKey === 'features' && <FeatureMatrixPanel />}

        {activeKey === 'suppliers' && <SupplierSelectionTab />}

        {activeKey === 'scoring' && supplierScoring && <SupplierScoringPanel supplierScoring={supplierScoring} />}

        {activeKey === 'current_suppliers' && <CurrentSuppliersPanel />}

        {activeKey === 'quotes' && <OfficialQuotesPanel />}

        {activeKey === 'pricing' && <PricingPanel />}

        {activeKey === 'roi' && (
          <RoiAnalysisPanel
            key={engagement?.id}
            onGoToCurrentSuppliers={currentSuppliersShown ? () => goTo('current_suppliers') : undefined}
          />
        )}

        {activeKey === 'data_room' && <DataRoomPanel />}

        {activeKey === 'action_plan' && <MutualActionPlanPanel />}
      </Box>

      {stepFlow && (
        <Box sx={{ borderTop: 1, borderColor: 'divider', mt: 3 }}>
          <EngagementStepFooter
            onBack={activeIndex > 0 ? () => leave(() => select(tabs[activeIndex - 1].key)) : stepFlow.onBack}
            onSaveAndQuit={stepFlow.onSaveAndQuit}
            saving={stepFlow.saving}
            onNext={activeIndex < tabs.length - 1 ? () => goTo(tabs[activeIndex + 1].key) : undefined}
          />
        </Box>
      )}

      {pending && hasUnansweredRequired(progress) && (
        <LeaveStepDialog
          open
          stepName={stepName(active)}
          destinationName={stepName(pending)}
          progress={progress}
          onStay={() => {
            setPendingKey(null);
            setStayRequests((count) => count + 1);
          }}
          onContinue={continueToPending}
        />
      )}
    </Box>
  );
};
